import moment from 'moment';
import { TableRow, TableCell, Tooltip, Checkbox, Chip } from '@material-ui/core';
import { Link } from 'react-router-dom';

import { BookingInformationIcon, BanIcon } from '../../icons';
import ActionIconButton from '../../components/atom/ActionIconButton';
import GuestActionButtons from '../../components/atom/GuestActionButtons';
import { useBookingPermissions } from '../../hooks/useBookingPermissions';

// Once custom_booking_id is set, the custom booking's own time is the real
// serving time — activity_start stays the original predefined slot (kept
// around so reverting to it doesn't need a separate snapshot). Same
// precedence rule as fullwidget's bookingsColumns.tsx.
function resolveActivityStart(booking_item) {
    if (booking_item?.custom_booking_id != null) {
        return booking_item?.custom_booking?.activity_start ?? booking_item?.activity_start;
    }
    return booking_item?.activity_start;
}

function BookingItemTableRow({
    booking_item,
    bulkSelecting,
    isSelected,
    handleSelectBooking,
    openSingleCancellationModal,
    loginAsCustomerWithId,
    showModal,
    customer,
    cartItemCount
}) {
    const { canRefund, refundDisabledReason } = useBookingPermissions(booking_item);

    let bInfo = {};
    try {
        bInfo = JSON.parse(booking_item?.booking_info);
    } catch (err) {
        console.log(err);
    }

    const isCancellable =
        booking_item.status !== 'cancelled' &&
        booking_item.status !== 'cancelled_full_refund' &&
        booking_item.status !== 'denied';

    // Cancel action requires both status check and refund capability
    const canCancel = isCancellable && canRefund;

    // Selectable for bulk = any active booking that can be refunded or cancelled
    const hasAmount = booking_item.amount > 0;
    const notFullyRefunded = booking_item.status !== 'cancelled_full_refund';
    const notDenied = booking_item.status !== 'denied';
    const canSelect = hasAmount && notFullyRefunded && notDenied && canRefund;

    let disabledReason = '';
    if (!hasAmount) {
        disabledReason = 'No refundable amount';
    } else if (!notFullyRefunded) {
        disabledReason = 'Already fully refunded';
    } else if (!notDenied) {
        disabledReason = 'Booking was denied';
    } else if (!canRefund) {
        disabledReason = refundDisabledReason;
    }

    return (
        <TableRow key={booking_item.id} selected={isSelected}>
            {bulkSelecting && (
                <TableCell padding="checkbox">
                    <Tooltip title={!canSelect ? disabledReason : ''}>
                        <span>
                            <Checkbox
                                checked={isSelected}
                                onChange={() => handleSelectBooking(booking_item.id)}
                                disabled={!canSelect}
                            />
                        </span>
                    </Tooltip>
                </TableCell>
            )}
            <TableCell>
                {booking_item?.booking?.user?.firstname
                    ? `${booking_item?.booking?.user?.firstname} ${booking_item?.booking?.user?.lastname}`
                    : ''}
            </TableCell>
            <TableCell>
                {booking_item?.booking?.agent?.firstname} {booking_item?.booking?.agent?.lastname}
                {booking_item?.booking?.agent?.user_groups?.length > 0 && (
                    <small>
                        <br /> ({booking_item?.booking?.agent?.user_groups?.join(', ')})
                    </small>
                )}
            </TableCell>
            <TableCell>{booking_item?.activity.company.name}</TableCell>
            <TableCell>{booking_item.id}</TableCell>
            <TableCell>
                {booking_item?.activity.name}
                {booking_item?.booking_channel && booking_item.booking_channel !== 'platform' && (
                    <Chip
                        size="small"
                        label={booking_item.booking_channel.toUpperCase()}
                        color="secondary"
                        style={{ marginLeft: 8 }}
                    />
                )}
                {booking_item?.timeshare_discount && (
                    <Link
                        to={`/client/bookings/${booking_item.timeshare_discount.timeshare_booking_item_id}`}
                        style={{ marginLeft: 8 }}
                    >
                        <Chip size="small" label="Timeshare" color="primary" clickable />
                    </Link>
                )}
            </TableCell>
            <TableCell>
                {moment
                    .utc(booking_item?.created_at)
                    .local()
                    .format('MM/DD/YYYY, h:mm a')}
            </TableCell>
            <TableCell>
                {moment.utc(resolveActivityStart(booking_item)).format('MM/DD/YYYY, h:mm a')}
            </TableCell>
            <TableCell>{bInfo?.confirmationCode ? `${bInfo.confirmationCode}` : '-'}</TableCell>
            <TableCell>{booking_item.status}</TableCell>
            <TableCell style={{ width: 170 }}>
                <ActionIconButton
                    title="Booking informations"
                    icon={BookingInformationIcon}
                    to={`/client/bookings/${booking_item.id}`}
                />
                <ActionIconButton
                    title={!canCancel && isCancellable ? refundDisabledReason : 'Cancel booking'}
                    icon={BanIcon}
                    disabled={!canCancel}
                    color={canCancel ? 'error' : undefined}
                    onClick={() => openSingleCancellationModal(booking_item)}
                />
                {booking_item?.booking?.user && (
                    <GuestActionButtons
                        customer={customer}
                        guestId={booking_item.booking.user?.id}
                        loginAsCustomerWithId={loginAsCustomerWithId}
                        cartItemCount={cartItemCount}
                        showModal={showModal}
                    />
                )}
            </TableCell>
        </TableRow>
    );
}

export default BookingItemTableRow;
